import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import Building3D from "@/components/Building3D";
import FloorPlan2D from "@/components/FloorPlan2D";
import SitePlan2D from "@/components/SitePlan2D";
import LegendPanel from "@/components/LegendPanel";
import SummaryHeader from "@/components/SummaryHeader";
import StorefrontEditor from "@/components/StorefrontEditor";
import UnitPlans from "@/components/UnitPlans";
import { exportBuildingPdf } from "@/utils/exportPdf";
import { exportInvestorPdf } from "@/utils/exportInvestorPdf";
import { captureAllSnapshots } from "@/utils/captureSnapshots";
import { captureInvestorSnapshots } from "@/utils/captureInvestorSnapshots";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Layers3, Map as MapIcon, AlertTriangle, DoorOpen,
  Building2, Sun, Camera, Download, Square, FileText, LandPlot, Sparkles, X, Megaphone,
  ShoppingBag, Ruler,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bump this version string whenever you push a new spec revision.
// Users who've dismissed an OLD version will see the new banner & toast again.
const SPEC_REVISION = "2026-09-05-three-floor-max-192-units";
const SPEC_HEADLINE = "REV · 3-floor max (no elevator) · 6 bars × 32 units = 192 res · 31% 2BR (60 units) · Commercial plaza ground-floor only · 93 stalls compliant";

// Single-bar presets — building local frame centroid ≈ (4, 13, 200).
// Tuned so the 320'×8'×27' bar fills the frame; App.js re-centers per-building.
const PRESETS = {
  // Camera placed at LOW Z (south side) so high-Z (north / entrance) appears at
  // the BACK of the view — matches the Site Plan orientation (N ↑).
  default:   { label: "Default 3/4",     pos: [80, 70, -260],  target: [4, 13, 200] },
  isometric: { label: "Isometric",       pos: [180, 110, -260], target: [4, 13, 200] },
  topdown:   { label: "Top-down",        pos: [4, 480, -340],   target: [4, 0, 200] },
  front:     { label: "South elevation", pos: [4, 25, -340],   target: [4, 13, 200] },
  roof:      { label: "Roof / Solar",    pos: [4, 140, -200],  target: [4, 30, 200] },
};

// Wider presets for the WHOLE SITE composite — bars span z=10..306, strip at z≈320.
// Camera at LOW (negative) Z looking toward +Z so the entrance / retail strip
// renders at the FAR END of the view (matches the Site Plan: N ↑).
const PRESETS_SITE = {
  // Camera looking from due SOUTH toward due NORTH, so the layout maps 1:1 with
  // the 2D Site Plan: south of project at BOTTOM of screen, retail strip at TOP.
  default:   { label: "Default 3/4",     pos: [148, 300, -360], target: [148, 30, 160] },
  isometric: { label: "Isometric",       pos: [400, 380, -340], target: [148, 30, 160] },
  topdown:   { label: "Top-down",        pos: [148, 800, -540], target: [148, 0, 160] },
  front:     { label: "South elevation", pos: [148, 60, -500],  target: [148, 30, 160] },
  roof:      { label: "Roof / Solar",    pos: [148, 400, -200], target: [148, 35, 160] },
};

const BUILDING_OPTIONS = [
  { id: "B1",   label: "B1",          icon: Building2 },
  { id: "B2",   label: "B2",          icon: Building2 },
  { id: "B3",   label: "B3",          icon: Building2 },
  { id: "B4",   label: "B4",          icon: Building2 },
  { id: "B5",   label: "B5",          icon: Building2 },
  { id: "B6",   label: "B6",          icon: Building2 },
  { id: "CP",   label: "PLAZA",       icon: ShoppingBag },
  { id: "SITE", label: "WHOLE SITE",  icon: LandPlot },
  { id: "UNITS", label: "UNITS",      icon: Ruler },
];

export default function App() {
  const [buildingId, setBuildingId] = useState("B1");
  const [building, setBuilding] = useState(null);
  const [summary, setSummary] = useState(null);
  const [siteSummary, setSiteSummary] = useState(null);
  const [activeFloor, setActiveFloor] = useState(1);
  const [view, setView] = useState("3d");
  const [showEgress, setShowEgress] = useState(true);
  const [transparent, setTransparent] = useState(false);
  const [showBalconies, setShowBalconies] = useState(true);
  const [showSolar, setShowSolar] = useState(false);
  const [preset, setPreset] = useState("default");
  const [bannerOpen, setBannerOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(`sunrise-banner-dismissed-${SPEC_REVISION}`) !== "1";
  });

  // One-time toast per spec revision.
  // NOTE: SPEC_REVISION / SPEC_HEADLINE are module-level constants (stable refs),
  // so the empty dep array is intentional — eslint flags them but they cannot change.
  useEffect(() => {
    const seenKey = `sunrise-spec-seen-${SPEC_REVISION}`;
    // localStorage here stores ONLY UI state (banner dismiss + toast-seen flag).
    // No PII or auth tokens — safe to keep in localStorage even with XSS concerns.
    if (localStorage.getItem(seenKey)) return undefined;
    const id = setTimeout(() => {
      toast(SPEC_HEADLINE, {
        icon: "📐",
        duration: 8000,
        description: "Click anywhere to dismiss",
      });
      localStorage.setItem(seenKey, "1");
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissBanner = () => {
    setBannerOpen(false);
    localStorage.setItem(`sunrise-banner-dismissed-${SPEC_REVISION}`, "1");
  };

  useEffect(() => {
    // API + axios are module-level / imports (stable). Only buildingId can change.
    if (buildingId === "UNITS") return;   // UNITS tab is a static reference view — no backend call needed
    (async () => {
      try {
        const [b, s, site] = await Promise.all([
          axios.get(`${API}/building`, { params: { building: buildingId } }),
          axios.get(`${API}/summary`,  { params: { building: buildingId } }),
          axios.get(`${API}/site-summary`),
        ]);
        setBuilding(b.data);
        setSummary(s.data);
        setSiteSummary(site.data);
      } catch (e) {
        console.error("load failed", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  const downloadCsv = async () => {
    try {
      const resp = await axios.get(`${API}/export/csv`, {
        params: { building: buildingId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sunrise_${buildingId.toLowerCase()}_unit_schedule.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported Building ${buildingId} CSV`);
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const downloadPdf = async () => {
    const originalView = view;
    const originalFloor = activeFloor;
    const originalPreset = preset;
    try {
      const loadingToast = toast.loading("Capturing 3D, 2D & Site snapshots…");
      const snapshots = await captureAllSnapshots({
        setView, setActiveFloor, setPreset,
        onProgress: (i, total, title) => {
          toast.loading(`Capturing (${i}/${total}) · ${title}`, { id: loadingToast });
        },
      });
      setView(originalView); setActiveFloor(originalFloor); setPreset(originalPreset);
      toast.loading("Building PDF…", { id: loadingToast });
      await exportBuildingPdf(building, summary, buildingId, snapshots, siteSummary);
      toast.success(`Exported Manufacturer Quote Package (Building ${buildingId})`, { id: loadingToast });
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed");
    }
  };

  const downloadInvestorPdf = async () => {
    const originalView = view;
    const originalFloor = activeFloor;
    const originalPreset = preset;
    try {
      const t = toast.loading("Capturing investor snapshots…");
      const snaps = await captureInvestorSnapshots({ setView, setActiveFloor, setPreset });
      setView(originalView); setActiveFloor(originalFloor); setPreset(originalPreset);
      toast.loading("Building investor one-pager…", { id: t });
      await exportInvestorPdf(building, summary, buildingId, snaps, siteSummary);
      toast.success("Exported Investor One-Pager", { id: t });
    } catch (e) {
      console.error(e);
      toast.error("Investor PDF export failed");
    }
  };

  if (!building) {
    return (
      <div data-testid="loading-screen" className="min-h-screen bg-[#0b0f14] text-[#e6edf3] grid place-items-center font-mono">
        <div className="text-xs tracking-[0.3em] opacity-60">LOADING BUILDING DATA…</div>
      </div>
    );
  }

  // Union floors across all wings, deduped by level, sorted ascending.
  // All wings are 3 floors max (no elevator past L3 — cost prohibitive).
  const floorsByLevel = new Map();
  (building.wings || []).forEach((w) => {
    (w.floors || []).forEach((f) => {
      if (!floorsByLevel.has(f.level)) floorsByLevel.set(f.level, f);
    });
  });
  const floors = Array.from(floorsByLevel.values()).sort((a, b) => a.level - b.level);
  const floor = floors.find((f) => f.level === activeFloor) || floors[0];
  const unitTypes = Object.fromEntries(building.unit_types.map((u) => [u.id, u]));
  const isSiteView = buildingId === "SITE";
  const presetMap = isSiteView ? PRESETS_SITE : PRESETS;
  let activePreset = presetMap[preset] || presetMap.default;

  // For single-building views, the bar/plaza may sit anywhere along the site x-axis.
  // Re-center the camera target on the building's actual centroid.
  if (!isSiteView && building.wings?.length) {
    const w = building.wings[0];
    // Wing centroid in world coords (rotation-aware): origin + half of (length-along-x, depth-along-z)
    const r = ((w.rotation_deg || 0) * Math.PI) / 180;
    const cx = w.origin_x + (w.length_ft / 2) * Math.cos(r) - (w.depth_ft / 2) * Math.sin(r);
    const cz = w.origin_z + (w.length_ft / 2) * Math.sin(r) + (w.depth_ft / 2) * Math.cos(r);
    const dx = cx - activePreset.target[0];
    const dz = cz - activePreset.target[2];
    activePreset = {
      ...activePreset,
      target: [cx, activePreset.target[1], cz],
      pos:    [activePreset.pos[0] + dx, activePreset.pos[1], activePreset.pos[2] + dz],
    };
  }

  return (
    <div data-testid="app-root" className="min-h-screen bg-[#0b0f14] text-[#e6edf3] font-mono selection:bg-amber-300 selection:text-black">
      {bannerOpen && (
        <div
          data-testid="spec-revision-banner"
          className="bg-amber-400 text-black border-b-2 border-amber-500"
        >
          <div className="max-w-[1600px] mx-auto px-6 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Megaphone className="w-4 h-4 flex-shrink-0" />
              <div className="text-xs sm:text-sm font-bold tracking-wide truncate">
                <span className="hidden sm:inline">WHAT&rsquo;S NEW · </span>
                {SPEC_HEADLINE}
              </div>
            </div>
            <button
              data-testid="banner-dismiss-btn"
              onClick={dismissBanner}
              className="flex-shrink-0 w-7 h-7 grid place-items-center rounded hover:bg-black/15 transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <header data-testid="app-header" className="border-b border-[#1f2937] bg-[#0b0f14]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-4">
            <div className="text-amber-400 text-xs tracking-[0.4em]">SHEET A1.0</div>
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
              {buildingId === "UNITS" ? "Unit Plan Dimensions" : building.name}
            </h1>
            <div className="hidden lg:block text-xs text-[#7d8b9b] tracking-widest">
              {buildingId === "UNITS"
                ? "· STUDIO · 1BR · 2BR · dimensioned drawings"
                : `· ${summary?.residential_units || summary?.total_units} UNITS · ${(summary?.living_sf && summary.living_sf > 0 ? summary.living_sf : summary?.footprint_sf)?.toLocaleString()} SF`}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Building tabs — driven by BUILDING_OPTIONS */}
            <Tabs value={buildingId} onValueChange={setBuildingId}>
              <TabsList data-testid="building-toggle" className="bg-[#0f172a] border border-[#1f2937] p-1 h-8">
                {BUILDING_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const isSite = opt.id === "SITE";
                  return (
                    <TabsTrigger
                      key={opt.id}
                      data-testid={`building-tab-${opt.id}`}
                      value={opt.id}
                      className={`text-xs tracking-widest h-6 px-3 ${
                        isSite
                          ? "data-[state=active]:bg-cyan-400 data-[state=active]:text-black"
                          : "data-[state=active]:bg-amber-400 data-[state=active]:text-black"
                      }`}
                    >
                      <Icon className="w-3 h-3 mr-1" /> {opt.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
            <div className="w-px h-6 bg-[#1f2937]" />
            <Button
              data-testid="toggle-3d-btn"
              size="sm"
              onClick={() => setView("3d")}
              className={view === "3d" ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-transparent text-[#e6edf3] hover:bg-[#1f2937] border border-[#1f2937]"}
            >
              <Layers3 className="w-4 h-4 mr-1.5" /> 3D
            </Button>
            <Button
              data-testid="toggle-2d-btn"
              size="sm"
              onClick={() => setView("2d")}
              className={view === "2d" ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-transparent text-[#e6edf3] hover:bg-[#1f2937] border border-[#1f2937]"}
            >
              <MapIcon className="w-4 h-4 mr-1.5" /> 2D PLAN
            </Button>
            <Button
              data-testid="toggle-site-btn"
              size="sm"
              onClick={() => setView("site")}
              className={view === "site" ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-transparent text-[#e6edf3] hover:bg-[#1f2937] border border-[#1f2937]"}
            >
              <LandPlot className="w-4 h-4 mr-1.5" /> SITE
            </Button>
            <Button
              data-testid="export-csv-btn"
              size="sm"
              onClick={downloadCsv}
              className="bg-transparent text-amber-300 hover:bg-amber-400/10 border border-amber-400/40"
            >
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
            <Button
              data-testid="export-pdf-btn"
              size="sm"
              onClick={downloadPdf}
              className="bg-amber-400/15 text-amber-300 hover:bg-amber-400/25 border border-amber-400/60"
            >
              <FileText className="w-4 h-4 mr-1.5" /> QUOTE PDF
            </Button>
            <Button
              data-testid="export-investor-pdf-btn"
              size="sm"
              onClick={downloadInvestorPdf}
              className="bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/50"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> INVESTOR PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <section className="space-y-4">
          {buildingId !== "UNITS" && <SummaryHeader summary={summary} building={building} />}

          {/* Floor tabs row */}
          {buildingId === "UNITS" ? (
            <UnitPlans />
          ) : (
          <Tabs value={String(activeFloor)} onValueChange={(v) => setActiveFloor(Number(v))}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <TabsList data-testid="floor-tabs" className="bg-[#0f172a] border border-[#1f2937] p-1">
                {floors.map((f) => (
                  <TabsTrigger
                    data-testid={`floor-tab-${f.level}`}
                    key={f.level}
                    value={String(f.level)}
                    className="data-[state=active]:bg-amber-400 data-[state=active]:text-black text-xs tracking-widest px-4"
                  >
                    LVL {f.level} · {f.name.split("—")[1]?.trim() || f.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex items-center gap-2 flex-wrap">
                {view === "3d" && (
                  <Select value={preset} onValueChange={setPreset}>
                    <SelectTrigger data-testid="preset-select" className="h-8 w-[200px] bg-[#0f172a] border border-[#1f2937] text-xs tracking-widest text-amber-300">
                      <Camera className="w-3.5 h-3.5 mr-1.5" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f172a] border-[#1f2937] text-[#e6edf3]">
                      {Object.entries(presetMap).map(([k, v]) => (
                        <SelectItem data-testid={`preset-${k}`} key={k} value={k} className="text-xs">
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  data-testid="toggle-egress-btn"
                  size="sm"
                  onClick={() => setShowEgress((s) => !s)}
                  className={`border border-[#1f2937] bg-transparent hover:bg-[#1f2937] ${showEgress ? "text-amber-300" : "text-[#7d8b9b]"}`}
                >
                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                  EGRESS
                </Button>
                {view === "3d" && (
                  <>
                    <Button
                      data-testid="toggle-xray-btn"
                      size="sm"
                      onClick={() => setTransparent((s) => !s)}
                      className={`border border-[#1f2937] bg-transparent hover:bg-[#1f2937] ${transparent ? "text-cyan-300" : "text-[#7d8b9b]"}`}
                    >
                      <DoorOpen className="w-3.5 h-3.5 mr-1.5" /> X-RAY
                    </Button>
                    <Button
                      data-testid="toggle-balcony-btn"
                      size="sm"
                      onClick={() => setShowBalconies((s) => !s)}
                      className={`border border-[#1f2937] bg-transparent hover:bg-[#1f2937] ${showBalconies ? "text-emerald-300" : "text-[#7d8b9b]"}`}
                    >
                      <Square className="w-3.5 h-3.5 mr-1.5" /> BALCONY
                    </Button>
                    <Button
                      data-testid="toggle-solar-btn"
                      size="sm"
                      onClick={() => setShowSolar((s) => !s)}
                      className={`border border-[#1f2937] bg-transparent hover:bg-[#1f2937] ${showSolar ? "text-yellow-300" : "text-[#7d8b9b]"}`}
                    >
                      <Sun className="w-3.5 h-3.5 mr-1.5" /> SOLAR
                    </Button>
                  </>
                )}
              </div>
            </div>

            <Card data-testid="canvas-card" className="mt-4 bg-[#070b10] border-[#1f2937] overflow-hidden">
              {view === "3d" && (
                <Building3D
                  building={building}
                  activeFloor={activeFloor}
                  showEgress={showEgress}
                  transparent={transparent}
                  showBalconies={showBalconies}
                  showSolar={showSolar}
                  preset={activePreset}
                />
              )}
              {view === "2d" && (
                <FloorPlan2D
                  building={building}
                  activeFloor={activeFloor}
                  showEgress={showEgress}
                  showBalconies={showBalconies && activeFloor >= 2}
                />
              )}
              {view === "site" && (
                <SitePlan2D
                  siteSummary={siteSummary}
                  showEgress={showEgress}
                />
              )}
            </Card>
          </Tabs>
          )}
        </section>

        <aside data-testid="side-panel" className="space-y-4">
          {buildingId !== "UNITS" && (
            <LegendPanel building={building} summary={summary} activeFloor={activeFloor} />
          )}
          {buildingId === "CP" && <StorefrontEditor />}
        </aside>
      </main>

      <footer className="border-t border-[#1f2937] mt-10">
        <div className="max-w-[1600px] mx-auto px-6 py-4 text-[10px] tracking-[0.3em] text-[#5b6776] flex justify-between">
          <span>DRW 05-14-2026 · A1.0</span>
          <span>SUNRISE COMMUNITY · BELIZE · CONTAINER HOUSING</span>
        </div>
      </footer>

      <Toaster theme="dark" />
    </div>
  );
}
